import { usePromisedRequest as promiseRequest } from "../Common/request";
import { Mat } from "../Common/types";
import errorResolver from "./errorResolver";
import { resolveWithBody } from "./Utils/utils";

export const CANDIDATE = 0;
export const DELETED = 1;
export const DISQUALIFIED_NO_PAYMENT = 2;
export const DISQUALIFIED_FAILED_VERIFICATION = 3;
export const VERIFIED = 4;
export const MISSED_FIGHT = 5;
export const CANNOT_UNDO_DUE_TO_FINISHED_FIGHT = 6;
export const AUTO_DQ = 7;

export type VerificationCandidate = {
    id: number;
    firstName: string;
    lastName: string;
    academy: string;
    category: string;
    mat: Mat;
};

export type MatProgress = {
    mat: Mat;
    totalVerifications: number;
};

export type VerificationQueue = {
    competitors: VerificationCandidate[];
    overallProgress: MatProgress[];
};

export type VerificationState =
    | typeof CANDIDATE
    | typeof DELETED
    | typeof DISQUALIFIED_NO_PAYMENT
    | typeof DISQUALIFIED_FAILED_VERIFICATION
    | typeof VERIFIED
    | typeof MISSED_FIGHT
    | typeof CANNOT_UNDO_DUE_TO_FINISHED_FIGHT
    | typeof AUTO_DQ;

export enum VerificationTarget {
    Verified = "verified",
    DisqualifiedFailedVerification = "disqualified_failed_verification",
    AutoDQ = "auto_dq", //Not used in frontend but let's map types 1:1.
}

export type VerificationRequest = {
    selectedCompetitorId: number;
    target: VerificationTarget;
    eventRegistrationSpotNumber?: number | null;
};

export type VerificationLog = {
    logId: number;
    eventRegistrationId: number;
    firstName: string;
    lastName: string;
    academyName: string;
    branch: string;
    category: string;
    time: string;
    verificationState: VerificationState;
};

//Source: Panel\Fights\Verification\VerificationSettings
export type VerificationSettings = {
    earlyVerificationBlocked: boolean;
    earlyVerificationPeriod: number;
    queueVerificationBlocked: boolean;
    queueVerificationLimit: number;
    autoDqWhenCategoryBeginsEnabled: boolean;
};

export default {
    getVerificationSettings(eventId: number): Promise<VerificationSettings> {
        return promiseRequest<VerificationSettings>({
            url: `/api/admin/events/${eventId}/verification-settings`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    getVerificationLogs(eventId: number, query: string): Promise<VerificationLog[]> {
        let url = `/api/admin/events/${eventId}/verification-logs`;

        if (query !== "") {
            url += "?query=" + query;
        }

        return promiseRequest<VerificationLog[]>({
            url: url,
            method: "get",
            json: true,
        }).then(res => {
            if (res.statusCode !== 200) {
                throw Error("Could not fetch verification logs.");
            }

            return res.body;
        });
    },
    sendVerificationRequest(eventId: number, request: VerificationRequest): Promise<void> {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/verifications`,
            method: "post",
            json: {
                selectedCompetitorId: request.selectedCompetitorId,
                target: request.target,
                eventRegistrationSpotNumber: request.eventRegistrationSpotNumber ? Number(request.eventRegistrationSpotNumber) : null,
            },
        }).then(res => {
            errorResolver.throwSimpleError(res);

            if (res.statusCode !== 204) {
                throw Error("Verification error.");
            }
        });
    },
    undoVerification(eventId: number, competitorId: number) {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/verifications/${competitorId}`,
            method: "delete",
            json: true,
        }).then(res => {
            errorResolver.throwSimpleError(res);

            if (res.statusCode !== 204) {
                throw Error("Could not undo verification of competitor.");
            }
        });
    },
    fetchVerificationQueue(eventId: number, matIdsQuery: string): Promise<VerificationQueue> {
        return promiseRequest<VerificationQueue>({
            url: `/api/admin/events/${eventId}/verifications?matIds=${matIdsQuery}`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
};
